/**
 * <admira-file-upload> — an optional file field for a Shopify contact form.
 *
 * A contact form cannot carry files, so the file goes to the `admira-uploads`
 * Worker (workers/admira-uploads) the moment it is chosen, and the link the
 * Worker returns is what the form sends, in a hidden `contact[…]` input. The
 * file input itself has no `name`, so the form never posts it.
 *
 * While an upload runs the element carries `data-upload-pending`;
 * `<admira-request-form>` waits for it to clear before submitting, so a quick
 * click on "Trimite" cannot send the request without the link.
 *
 * Checks size and type before uploading to save a round trip; the Worker
 * checks again from the bytes. Messages are locale strings passed in as data
 * attributes.
 */

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const ACCEPTED_EXT = /\.(png|jpe?g|webp|svg)$/i;

class AdmiraFileUpload extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLInputElement | null} */
  #output = null;
  /** @type {AbortController | null} */
  #request = null;
  /** @type {string} */
  #preview = '';

  connectedCallback() {
    this.#input = this.querySelector('input[type="file"]');
    this.#output = this.querySelector('[data-upload-url]');
    this.#input?.addEventListener('change', this.#onChange);
    this.querySelector('[data-upload-clear]')?.addEventListener('click', this.#clear);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('change', this.#onChange);
    this.#request?.abort();
    if (this.#preview) URL.revokeObjectURL(this.#preview);
  }

  #onChange = async () => {
    const file = this.#input?.files?.[0];
    this.#request?.abort();
    this.#setLink('');
    if (!file) return this.#show('idle');

    const max = Number(this.dataset.maxBytes ?? 0);
    if (max && file.size > max) return this.#show('error', this.dataset.errorSize);
    if (!ACCEPTED.includes(file.type) && !ACCEPTED_EXT.test(file.name)) return this.#show('error', this.dataset.errorType);

    const endpoint = this.dataset.endpoint;
    if (!endpoint) return this.#show('error', this.dataset.errorNetwork);

    const request = new AbortController();
    this.#request = request;
    this.setAttribute('data-upload-pending', '');
    this.#show('uploading', this.dataset.uploading);

    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(endpoint, { method: 'POST', body, signal: request.signal });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.url) {
        const reasons = { size: this.dataset.errorSize, type: this.dataset.errorType, rate: this.dataset.errorRate };
        this.#show('error', reasons[/** @type {keyof reasons} */ (result.error)] ?? this.dataset.errorNetwork);
        return;
      }

      this.#setLink(result.url);
      this.#show('done', (this.dataset.done ?? '{{ name }}').replace('{{ name }}', file.name), file);
    } catch (error) {
      if (request.signal.aborted) return;
      console.error('admira-file-upload:', error);
      this.#show('error', this.dataset.errorNetwork);
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.removeAttribute('data-upload-pending');
      }
    }
  };

  #clear = () => {
    this.#request?.abort();
    this.removeAttribute('data-upload-pending');
    if (this.#input) this.#input.value = '';
    this.#setLink('');
    this.#show('idle');
    this.#input?.focus();
  };

  /** @param {string} url */
  #setLink(url) {
    if (this.#output) this.#output.value = url;
  }

  /**
   * @param {'idle' | 'uploading' | 'done' | 'error'} state
   * @param {string} [message]
   * @param {File} [file] - shown as a thumbnail once uploaded
   */
  #show(state, message = '', file) {
    this.dataset.state = state;

    const status = this.querySelector('[data-upload-status]');
    if (status) status.textContent = message;

    const row = this.querySelector('[data-upload-row]');
    if (row instanceof HTMLElement) row.hidden = state === 'idle';

    const thumb = this.querySelector('[data-upload-thumb]');
    if (thumb instanceof HTMLImageElement) {
      if (this.#preview) URL.revokeObjectURL(this.#preview);
      this.#preview = file ? URL.createObjectURL(file) : '';
      if (this.#preview) thumb.src = this.#preview;
      else thumb.removeAttribute('src');
      thumb.hidden = !this.#preview;
    }

    if (this.#input) {
      if (state === 'error') this.#input.setAttribute('aria-invalid', 'true');
      else this.#input.removeAttribute('aria-invalid');
    }
  }
}

if (!customElements.get('admira-file-upload')) {
  customElements.define('admira-file-upload', AdmiraFileUpload);
}
